import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Product, ProductVariant } from '@/types/database'

/**
 * Cart item stored in the cart
 * Only stores IDs and quantity - pricing is resolved at checkout
 */
export interface CartItem {
  /** Product ID */
  productId: string
  /** Variant ID (null = no variant / product has no options) */
  variantId: string | null
  quantity: number
  addedAt: number // timestamp for sorting
  /** Customer-provided values for custom input fields */
  customInputs?: Record<string, string>
}

/**
 * Cart item with full product details (for display)
 */
export interface CartItemWithProduct extends CartItem {
  product: Product
}

/** Composite key for identifying a cart item (product + variant) */
function cartItemKey(productId: string, variantId: string | null): string {
  return variantId ? `${productId}::${variantId}` : productId
}

interface CartState {
  // Cart data
  items: CartItem[]
  isOpen: boolean

  // Actions
  addItem: (productId: string, variantId?: string | null, quantity?: number, customInputs?: Record<string, string>) => void
  removeItem: (productId: string, variantId?: string | null) => void
  updateQuantity: (productId: string, variantId: string | null, quantity: number) => void
  clearCart: () => void
  toggleCart: () => void
  openCart: () => void
  closeCart: () => void

  // Getters
  getItemCount: () => number
  getItem: (productId: string, variantId?: string | null) => CartItem | undefined
  hasItem: (productId: string, variantId?: string | null) => boolean
}

/**
 * Migrate old cart items that may lack variantId
 */
function migrateCartItem(item: any): CartItem {
  return {
    productId: item.productId || item.productionId,
    variantId: item.variantId ?? null,
    quantity: item.quantity,
    addedAt: item.addedAt || Date.now(),
    customInputs: item.customInputs,
  }
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      // Initial state
      items: [],
      isOpen: false,

      // Add item to cart (or increment quantity if exists)
      addItem: (productId: string, variantId: string | null = null, quantity = 1, customInputs?: Record<string, string>) => {
        const items = get().items
        const key = cartItemKey(productId, variantId)
        const existingIndex = items.findIndex(item =>
          cartItemKey(item.productId, item.variantId) === key
        )

        if (existingIndex >= 0) {
          // Update quantity of existing item
          const newItems = [...items]
          newItems[existingIndex] = {
            ...newItems[existingIndex],
            quantity: newItems[existingIndex].quantity + quantity,
            // Update custom inputs if provided
            ...(customInputs && { customInputs }),
          }
          set({ items: newItems })
        } else {
          // Add new item
          set({
            items: [
              ...items,
              {
                productId,
                variantId,
                quantity,
                addedAt: Date.now(),
                customInputs,
              },
            ],
          })
        }
      },

      // Remove item from cart
      removeItem: (productId: string, variantId: string | null = null) => {
        const key = cartItemKey(productId, variantId)
        set({
          items: get().items.filter(item =>
            cartItemKey(item.productId, item.variantId) !== key
          ),
        })
      },

      // Update quantity (removes if quantity <= 0)
      updateQuantity: (productId: string, variantId: string | null, quantity: number) => {
        if (quantity <= 0) {
          get().removeItem(productId, variantId)
          return
        }

        const items = get().items
        const key = cartItemKey(productId, variantId)
        const existingIndex = items.findIndex(item =>
          cartItemKey(item.productId, item.variantId) === key
        )

        if (existingIndex >= 0) {
          const newItems = [...items]
          newItems[existingIndex] = {
            ...newItems[existingIndex],
            quantity,
          }
          set({ items: newItems })
        }
      },

      // Clear all items
      clearCart: () => {
        set({ items: [] })
      },

      // Toggle cart drawer
      toggleCart: () => {
        set({ isOpen: !get().isOpen })
      },

      // Open cart drawer
      openCart: () => {
        set({ isOpen: true })
      },

      // Close cart drawer
      closeCart: () => {
        set({ isOpen: false })
      },

      // Get total item count (sum of quantities)
      getItemCount: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0)
      },

      // Get specific item
      getItem: (productId: string, variantId: string | null = null) => {
        const key = cartItemKey(productId, variantId)
        return get().items.find(item =>
          cartItemKey(item.productId, item.variantId) === key
        )
      },

      // Check if item is in cart
      hasItem: (productId: string, variantId: string | null = null) => {
        const key = cartItemKey(productId, variantId)
        return get().items.some(item =>
          cartItemKey(item.productId, item.variantId) === key
        )
      },
    }),
    {
      name: 'ion-cart', // localStorage key
      partialize: (state) => ({
        // Only persist cart items, not UI state
        items: state.items,
      }),
      // Migrate old cart data on load
      onRehydrateStorage: () => (state) => {
        if (state?.items) {
          state.items = state.items.map(migrateCartItem)
        }
      },
    }
  )
)

// Selector hooks for common patterns
export const useCartItems = () => useCartStore(state => state.items)
export const useCartIsOpen = () => useCartStore(state => state.isOpen)
export const useCartItemCount = () => useCartStore(state => state.getItemCount())

/**
 * Resolve variant from a product's variants JSONB array
 */
export function resolveVariant(product: Product, variantId: string | null): ProductVariant | null {
  if (!variantId) return null
  const variants = (product.variants as unknown as ProductVariant[]) || []
  return variants.find(v => v.id === variantId) || null
}

/**
 * Get the effective price for a product (with optional variant adjustment)
 */
export function getEffectivePrice(product: Product, variant: ProductVariant | null): number {
  const base = product.original_price + (variant?.price_adjustment || 0)
  return base
}

/**
 * Build a human-readable label from variant option values
 */
export function getVariantLabel(variant: ProductVariant | null): string {
  if (!variant) return ''
  return Object.entries(variant.option_values).map(([, v]) => v).join(' / ')
}

/**
 * Calculate cart totals given product data
 * This is done client-side for display; server recalculates at checkout
 */
export function calculateCartTotals(
  items: CartItem[],
  productMap: Map<string, Product>
): {
  subtotal: number
  originalTotal: number
  totalSavings: number
  itemCount: number
} {
  let subtotal = 0
  let originalTotal = 0
  let itemCount = 0

  for (const item of items) {
    const product = productMap.get(item.productId)
    if (!product) continue

    const variant = resolveVariant(product, item.variantId)
    const effectivePrice = getEffectivePrice(product, variant)

    const discountPercent = product.status === 'featured'
      ? product.featured_discount_percent
      : product.archive_discount_percent

    const discountedPrice = effectivePrice * (1 - discountPercent / 100)

    subtotal += discountedPrice * item.quantity
    originalTotal += effectivePrice * item.quantity
    itemCount += item.quantity
  }

  return {
    subtotal,
    originalTotal,
    totalSavings: originalTotal - subtotal,
    itemCount,
  }
}
